import React from 'react';
import { Link } from 'react-router-dom';
import { scrollToElement } from '../../utils/scrollUtils';

const Button = ({ 
  children, 
  href, 
  to, 
  variant = 'primary', 
  size = 'md', 
  className = '',
  icon,
  iconPosition = 'right',
  scrollTo,
  scrollOffset,
  scrollDelay = 0,
  ...props 
}) => {
  const baseStyles = 'font-medium rounded-md transition-colors inline-flex items-center justify-center whitespace-nowrap';
  
  const variantStyles = {
    primary: 'bg-primary text-white hover:bg-secondary',
    secondary: 'border-2 border-primary text-primary hover:bg-accent hover:border-accent',
    white: 'bg-white text-primary hover:bg-accent',
  };
  
  const sizeStyles = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-base font-semibold',
  };
  
  const buttonClasses = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;
  
  const content = (
    <>
      {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
      {children}
      {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
    </>
  );

  // Handle scrolling for internal links with hash
  const handleScroll = (e, shouldPreventDefault = true) => {
    if (scrollTo) {
      if (shouldPreventDefault) {
        e.preventDefault();
      }
      
      // For same-page links, scroll directly
      if (scrollTo === 'top') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Use our enhanced scroll utility with options
        scrollToElement(scrollTo, {
          smooth: true,
          delay: scrollDelay,
          offset: scrollOffset || 80 // Default 80px to account for header
        });
      }
    }
  };
  
  // For react-router Link components (internal navigation)
  if (to) {
    // Check if the link contains a hash
    const hasHash = to.includes('#');
    
    // If it's just a hash link on the same page
    if (hasHash && to.startsWith('#')) {
      const elementId = to.substring(1);
      return (
        <a 
          href={to}
          className={buttonClasses} 
          onClick={(e) => {
            e.preventDefault();
            scrollToElement(elementId, { 
              smooth: true,
              offset: scrollOffset || 80
            });
            if (props.onClick) props.onClick(e);
          }}
          {...props}
        >
          {content}
        </a>
      );
    }
    
    // For links to other pages with hash
    return (
      <Link 
        to={to} 
        className={buttonClasses} 
        onClick={(e) => {
          // The ScrollToTop component will handle this navigation
          // but we'll also call the original onClick if provided
          if (props.onClick) props.onClick(e);
        }}
        {...props}
      >
        {content}
      </Link>
    );
  }
  
  // For external links
  if (href) {
    return (
      <a 
        href={href} 
        className={buttonClasses} 
        onClick={(e) => {
          if (scrollTo) {
            handleScroll(e);
          } else if (props.onClick) {
            props.onClick(e);
          }
        }}
        {...props}
      >
        {content}
      </a>
    );
  }
  
  // For button elements
  return (
    <button 
      className={buttonClasses} 
      onClick={(e) => {
        if (scrollTo) {
          handleScroll(e, false);
          if (props.onClick) props.onClick(e);
        } else if (props.onClick) {
          props.onClick(e);
        }
      }}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button;
